import React, { MouseEvent, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Icons,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import {
  ASSIGNMENT_HIDE_TOOLTIP,
  ASSIGNMENT_UNHIDE_TOOLTIP,
  EXTENSION_HIDE_TOOLTIP,
  EXTENSION_HIDDEN_TOOLTIP,
  EXTENSION_UNHIDE_TOOLTIP,
  EXTENSION_VISIBLE_TOOLTIP,
} from '@app/constants/texts';
import type { SubmissionAssignment } from '../SubmissionsResponsive';

type Props = {
  extension: SubmissionAssignment;
  isExtension: boolean;
  canEditExtensions: boolean;
  selectedExtensionId: string;
  menuExtension: SubmissionAssignment | undefined;
  spaceId: string;
  accessToken?: string;
  openMenu: (
    event: MouseEvent<HTMLButtonElement>,
    extension: SubmissionAssignment
  ) => void;
  editExtension: (params: any) => Promise<SubmissionAssignment | null>;
  onSelect: (extension: SubmissionAssignment) => void;
  styles: any;
};

const ExtensionItem = ({
  extension,
  isExtension,
  selectedExtensionId,
  canEditExtensions,
  spaceId,
  accessToken,
  openMenu,
  editExtension,
  onSelect,
  menuExtension,
  styles,
}: Props) => {
  const theme = useTheme();
  const [nameRef, isOverflowing] = useIsOverflowing<HTMLDivElement>();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const titleTooltipDisabled = !isDesktop || !isOverflowing;
  const extensionName = isExtension ? extension.title : 'Main cohort';
  const canToggleVisibility = isExtension && canEditExtensions;
  const visibilityTooltip = isExtension
    ? extension.isHidden
      ? canToggleVisibility
        ? EXTENSION_UNHIDE_TOOLTIP
        : EXTENSION_HIDDEN_TOOLTIP
      : canToggleVisibility
      ? EXTENSION_HIDE_TOOLTIP
      : EXTENSION_VISIBLE_TOOLTIP
    : extension.isHidden
    ? ASSIGNMENT_UNHIDE_TOOLTIP
    : ASSIGNMENT_HIDE_TOOLTIP;
  const isSelected =
    selectedExtensionId === extension.id ||
    selectedExtensionId === extension.objectId;
  const extensionItemRef = useRef<HTMLLIElement | null>(null);

  // On mobile the list and detail views are separate routes. When returning to
  // the list, bring the restored selection into view just as the responsive
  // material and assignment lists do for their selected items.
  useEffect(() => {
    if (!isSelected || isDesktop || !extensionItemRef.current) return;

    const scrollToSelectedExtension = () => {
      extensionItemRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    };

    const rafId = window.requestAnimationFrame(scrollToSelectedExtension);
    return () => window.cancelAnimationFrame(rafId);
  }, [isDesktop, isSelected]);

  const onToggleVisibility = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!canToggleVisibility) return;
    editExtension({
      accessToken,
      assignment: {
        ...extension,
        isHidden: !extension.isHidden,
      },
      spaceId,
    });
  };

  return (
    <Box
      component="li"
      key={extension.id}
      ref={extensionItemRef}
      sx={{
        ...styles.extensionItem,
        ...(isDesktop && !isSelected ? styles.extensionItemHover : {}),
        ...(isDesktop ? styles.extensionItemActionHover : {}),
        ...(isSelected ? styles.extensionItemSelected : {}),
      }}
    >
      <Box
        component="button"
        aria-label={`View ${extensionName}`}
        aria-current={isSelected ? 'page' : undefined}
        sx={styles.extensionSelectButton}
        onClick={() => onSelect(extension)}
      >
        {isExtension && (
          <Icons.ExtensionOutlined
            fontSize="medium"
            sx={styles.extensionIcon}
            aria-hidden
          />
        )}
        <Box sx={styles.extensionTitle}>
          <Tooltip
            title={extensionName}
            disableFocusListener={titleTooltipDisabled}
            disableHoverListener={titleTooltipDisabled}
            disableTouchListener={titleTooltipDisabled}
          >
            <Box sx={styles.extensionName} component="div" ref={nameRef}>
              {extensionName}
            </Box>
          </Tooltip>
          <Box component="span" sx={styles.extensionCount}>
            ({extension.submissionsCount ?? 0}/{extension.studentsCount ?? 0})
          </Box>
        </Box>
      </Box>
      <Box sx={styles.extensionItemDetails}>
        {isExtension && canEditExtensions && (
          <Tooltip
            disableInteractive
            title="Extension options"
            disableFocusListener={!isDesktop}
            disableHoverListener={!isDesktop}
            disableTouchListener={!isDesktop}
          >
            <Box
              className="extension-action"
              component="span"
              sx={{
                ...styles.menuButtonWrapper,
                ...(menuExtension?.id === extension.id && {
                  display: 'inline-flex',
                }),
              }}
            >
              <IconButton
                id={`extension-options-${extension.id}`}
                type="button"
                aria-label={`Options for ${extension.title}`}
                aria-controls={
                  menuExtension?.id === extension.id
                    ? 'extension-options-menu'
                    : undefined
                }
                aria-expanded={menuExtension?.id === extension.id || undefined}
                aria-haspopup="menu"
                sx={styles.menuIcon}
                onClick={(event) => openMenu(event, extension)}
              >
                <Icons.MoreVert />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        <Tooltip
          title={visibilityTooltip}
          disableFocusListener={!isDesktop}
          disableHoverListener={!isDesktop}
          disableTouchListener={!isDesktop}
        >
          <Box component="span">
            <IconButton
              className="extension-action"
              type="button"
              disabled={!canToggleVisibility}
              aria-label={visibilityTooltip}
              sx={{
                ...styles.visibilityIcon,
                ...(extension.isHidden && { display: 'inline-flex' }),
              }}
              onClick={onToggleVisibility}
            >
              {extension.isHidden ? (
                <Icons.VisibilityOffOutlined />
              ) : (
                <Icons.VisibilityOutlined />
              )}
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ExtensionItem;
